/**
 * ReflectionSection Component
 * Inline AI reflection prompt for the My Rituality dashboard.
 * Shows a heading, AI-generated subtitle, and multiline input.
 * On submit, creates a new AI conversation and navigates to the chat page.
 */

import { Mic } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { SendIcon } from "@/components/icons/index"
import { createConversation } from "@/features/chats/services/ai-chat.service"
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition"
import { useTranslation } from "@/language"
import { cn } from "@/lib/utils"

export function ReflectionSection() {
  const { t } = useTranslation("myRituality")
  const navigate = useNavigate()
  const [message, setMessage] = useState("")
  const [isSending, setIsSending] = useState(false)

  const inputRef = useRef(message)
  useEffect(() => {
    inputRef.current = message
  }, [message])
  const handleTranscript = useCallback((text: string) => {
    const current = inputRef.current
    const next = current ? `${current.trimEnd()} ${text.trim()}` : text.trim()
    setMessage(next)
  }, [])
  const speech = useSpeechRecognition({ onTranscript: handleTranscript })

  const handleSend = async () => {
    if (!message.trim() || isSending) return

    try {
      setIsSending(true)
      const conversation = await createConversation()
      navigate(`/chats/ai/${conversation.id}?firstMessage=${encodeURIComponent(message.trim())}`)
    } catch {
      toast.error(t("reflection.error"))
      setIsSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <section>
      {/* Heading */}
      <h2 className="text-lg md:text-2xl font-semibold leading-6.25 md:leading-7.5 uppercase opacity-90 text-primary">
        {t("reflection.title")}
      </h2>

      {/* Input area */}
      <div className="relative mt-5 bg-[#020202] md:bg-white/5 border border-white/15 rounded-[15px] overflow-hidden">
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("reflection.placeholder")}
          className="w-full h-[140px] bg-transparent text-sm text-primary placeholder:text-primary/50 p-3.5 pr-24 resize-none focus:outline-none"
          disabled={isSending}
        />

        {/* Action buttons */}
        <div className="absolute bottom-4 right-4 flex items-center gap-3">
          {speech.isSupported && (
            <button
              type="button"
              onClick={speech.toggle}
              disabled={isSending}
              aria-label={speech.isRecording ? "Stop recording" : "Voice input"}
              aria-pressed={speech.isRecording}
              className={cn(
                "p-2 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed",
                speech.isRecording
                  ? "text-red-500 animate-pulse"
                  : "text-primary/50 hover:text-primary",
              )}
            >
              <Mic className="w-[25px] h-[25px]" />
            </button>
          )}
          <button
            type="button"
            onClick={handleSend}
            disabled={!message.trim() || isSending}
            className="text-primary/50 hover:text-primary transition-colors disabled:opacity-30 cursor-pointer"
            aria-label={t("reflection.send")}
          >
            <SendIcon className="w-[23px] h-[23px]" />
          </button>
        </div>
      </div>
    </section>
  )
}
